/**
 * Currency utilities backed by ISO 4217 (currency-codes package).
 *
 * GBX (pence sterling) is not an official ISO 4217 code but is used by
 * some brokers (e.g. Trading 212) for UK-listed equities, so we treat it
 * as a valid code via explicit allowlisting.
 */
import cc from 'currency-codes';

const NON_ISO_CODES: ReadonlySet<string> = new Set(['GBX']);

/** Returns true for any valid ISO 4217 code or known broker-specific code (GBX). */
export function isValidCurrencyCode(code: string): boolean {
  const upper = code.toUpperCase();
  return NON_ISO_CODES.has(upper) || cc.code(upper) !== undefined;
}

/**
 * Returns the human-readable currency name, e.g. "Euro" for "EUR".
 * Returns undefined for codes not in ISO 4217 (use the code itself as fallback).
 */
export function getCurrencyName(code: string): string | undefined {
  const upper = code.toUpperCase();
  if (NON_ISO_CODES.has(upper)) return 'Pence Sterling';
  return cc.code(upper)?.currency;
}

/** All ISO 4217 codes plus GBX, sorted alphabetically. */
export function allCurrencyCodes(): string[] {
  return [...cc.codes(), 'GBX'].sort();
}

/**
 * Curated subset of currencies shown in the app's dropdown.
 * The API accepts ANY valid ISO 4217 code (or GBX); this list is for UI only.
 */
export const SUPPORTED_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'PLN',
  'GBX',
  'CHF',
  'CAD',
  'AUD',
] as const;
export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];
